import { DeviceInfo } from '@capacitor/device';
import { OperatingSystem } from '@capacitor/device/dist/esm/definitions';
import { createReducer, on } from '@ngrx/store';

import { map } from 'lodash-es';

import * as fromDataProviderActions from '../actions/data-provider.actions';
import * as fromDeviceActions from '../actions/device.actions';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromSettingActions from '../actions/setting.actions';

export const deviceFeatureKey = 'device';

export interface State {
  appName: string;
  appVersion: string;
  deviceData: string;
  deviceDimensions: string;
  deviceType: 'ios' | 'android' | 'web' | '';
  info: DeviceInfo | null;
  iOSVersion: number | undefined;
  model: string | undefined;
  officeCode: string;
  os: OperatingSystem | '';
  osVersion: string;
  userName: string;
  uuid: string | null;
}

export const initialState: State = {
  appName: '',
  appVersion: '7.5.0 (777)',
  deviceData: 'APPNAME|,APPVERSION|,DEVICEDIMENSSIONS|,DEVICETYPE|,OFICECODE|,OS|,OSVERSION|,USERNAME|,',
  deviceDimensions: '',
  deviceType: '',
  info: null,
  iOSVersion: undefined,
  model: undefined,
  officeCode: '',
  os: '',
  osVersion: '',
  userName: '',
  uuid: null
};

export const reducer = createReducer(
  initialState,
  on(fromDeviceActions.loadDeviceInfoSuccess, (state, { payload }) => ({
    ...state,
    deviceType: payload.info.platform,
    info: payload.info,
    iOSVersion: payload.info.iOSVersion,
    model: payload.info.model,
    os: payload.info.operatingSystem,
    osVersion: payload.info.osVersion,
    uuid: payload.uuid
  })),
  on(fromDeviceActions.loadAppInfoSuccess, (state, { payload }) => ({
    ...state,
    appVersion: `${payload.appInfo.version} (${payload.appInfo.build})`
  })),
  on(fromDeviceActions.loadAppInfoFailure, (state) => ({
    ...state,
    appVersion: initialState.appVersion
  })),
  on(fromSettingActions.loadSettings, (state, { payload }) => ({
    ...state,
    officeCode: payload.referralCode
  })),
  on(
    fromSettingActions.updateReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload }) => ({
      ...state,
      appName: payload.data.organization.applicationName,
      officeCode: payload.code
    })
  ),
  on(
    fromSettingActions.updateReferralCodeSuccess,
    fromSettingActions.loadSettingsSuccess,
    fromSettingActions.refreshReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload }) => ({
      ...state,
      appName: payload.data.organization.applicationName
    })
  ),
  on(
    fromDataProviderActions.authenticateDataProviderSuccess,
    fromPersistedStateActions.migratePersistedOldStateDataProviderCredentialsSuccess,
    (state, { payload }) => ({
      ...state,
      userName: payload.data.username ?? ''
    })
  ),
  on(fromDeviceActions.updateDeviceDimensions, (state, { payload }) => ({
    ...state,
    deviceDimensions: `${payload.width}x${payload.height}`
  })),
  on(
    fromDeviceActions.loadDeviceInfoSuccess,
    fromSettingActions.loadSettingsSuccess,
    fromSettingActions.updateReferralCodeSuccess,
    fromDataProviderActions.authenticateDataProviderSuccess,
    fromDeviceActions.updateDeviceDimensions,
    fromPersistedStateActions.persistedStateReset,
    fromPersistedStateActions.loadPersistedStateSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateDataProviderCredentialsSuccess,
    fromDeviceActions.loadAppInfoSuccess,
    fromDeviceActions.loadAppInfoFailure,
    (state) => ({
      ...state,
      deviceData: [
        map(
          [
            ['APPNAME', state.appName],
            ['APPVERSION', state.appVersion],
            ['DEVICEDIMENSIONS', state.deviceDimensions],
            ['DEVICETYPE', state.deviceType],
            ['OFFICECODE', state.officeCode],
            ['OS', state.os],
            ['OSVERSION', state.osVersion],
            ['USERNAME', state.userName]
          ],
          (item) => item.join('|')
        )
      ].join(',')
    })
  ),
  on(fromPersistedStateActions.persistedStateReset, fromPersistedStateActions.loadPersistedStateFailure, (state) => ({
    ...state,
    appName: initialState.appName,
    officeCode: initialState.officeCode,
    userName: initialState.userName
  })),
  on(fromPersistedStateActions.loadPersistedStateSuccess, (state, { payload }) => ({
    ...state,
    ...payload[deviceFeatureKey]
  }))
);
